import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { GENERAL_ERROR_MESSAGE } from '../../constants';
import optionsAPI from '../../api/optionsAPI';
import getAsyncErrorMessage from '../../utils/getAsyncErrorMessage';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../store';
import type { RejectedWithValueAction } from '../../types/common';
import type { GetCustomerParams, OptionsState } from '../../types/options';

export const initialState: OptionsState = {
  isLoading: false,
  hasError: false,
  serverMessage: undefined,
  organizationBase: [],
  steelMaker: [],
  businessType: [],
  steelType: [],
  steelCategory: [],
  steelCategoryType: [],
  customer: [],
  customer1: [],
  customer2: [],
  customer3: [],
  language: [],
  tradingCompany: [],
};

const slice = createSlice({
  name: 'Options',
  initialState,
  reducers: {
    startLoading(state: OptionsState) {
      state.isLoading = true;
    },

    hasError(state: OptionsState, action: PayloadAction<string>) {
      // console.log(action.payload.response);
      const message = action.payload;
      state.isLoading = false;
      state.hasError = true;
      state.serverMessage = message;
    },

    resetMessage(state: OptionsState) {
      state.isLoading = false;
      state.serverMessage = undefined;
      state.hasError = false;
    },

    setServerMessage(state: OptionsState, action: PayloadAction<string>): void {
      state.isLoading = false;
      state.serverMessage = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      // Get OrganizationBase
      // ----------------------------------------
      .addCase(getOrganizationBaseOptions.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getOrganizationBaseOptions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organizationBase = action.payload;
      })
      .addCase(getOrganizationBaseOptions.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<undefined, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
      })
      // Get SteelMaker
      // ----------------------------------------
      .addCase(getSteelMakerOptions.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getSteelMakerOptions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.steelMaker = action.payload;
      })
      .addCase(getSteelMakerOptions.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<undefined, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
      })
      // Get BusinessType
      // ----------------------------------------
      .addCase(getBusinessTypeOptions.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getBusinessTypeOptions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.businessType = action.payload;
      })
      .addCase(getBusinessTypeOptions.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<undefined, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
      })
      // Get SteelCategory
      // ----------------------------------------
      .addCase(getSteelCategoryOptions.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getSteelCategoryOptions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.steelCategory = action.payload;
      })
      .addCase(getSteelCategoryOptions.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<undefined, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
      })
      // Get SteelCategory and Type
      // ----------------------------------------
      .addCase(getSteelCategoryTypeOptions.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getSteelCategoryTypeOptions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.steelCategoryType = action.payload;
      })
      .addCase(getSteelCategoryTypeOptions.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<undefined, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
      })
      // Get Customer
      // ----------------------------------------
      .addCase(getCustomerOptions.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getCustomerOptions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customer = action.payload;
      })
      .addCase(getCustomerOptions.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<undefined, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
      })
      // Get Customer1
      // ----------------------------------------
      .addCase(getCustomer1Options.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getCustomer1Options.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customer1 = action.payload;
      })
      .addCase(getCustomer1Options.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<undefined, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
      })
      // Get Customer2
      // ----------------------------------------
      .addCase(getCustomer2Options.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getCustomer2Options.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customer2 = action.payload;
      })
      .addCase(getCustomer2Options.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<undefined, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
      })
      // Get Customer3
      // ----------------------------------------
      .addCase(getCustomer3Options.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getCustomer3Options.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customer3 = action.payload;
      })
      .addCase(getCustomer3Options.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<undefined, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
      })
      // Get Language
      // ----------------------------------------
      .addCase(getLanguageOptions.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getLanguageOptions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.language = action.payload;
      })
      .addCase(getLanguageOptions.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<undefined, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
      })
      // Get Trading Company
      // ----------------------------------------
      .addCase(getTradingCompanyOptions.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getTradingCompanyOptions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tradingCompany = action.payload;
      })
      .addCase(getTradingCompanyOptions.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<undefined, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
      });
  },
});

export const { reducer, actions } = slice;

export const resetMessage =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.resetMessage());
  };

export const getOrganizationBaseOptions = createAsyncThunk('Options/getOrganizationBaseOptions', async (_, thunkAPI) => {
  try {
    const response = await optionsAPI.getOrganizationBaseOptions();
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const getSteelMakerOptions = createAsyncThunk('Options/getSteelMakerOptions', async (_, thunkAPI) => {
  try {
    const response = await optionsAPI.getSteelMakerOptions();
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const getBusinessTypeOptions = createAsyncThunk('Options/getBusinessTypeOptions', async (_, thunkAPI) => {
  try {
    const response = await optionsAPI.getBusinessTypeOptions();
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const getSteelCategoryOptions = createAsyncThunk('Options/getSteelCategoryOptions', async (_, thunkAPI) => {
  try {
    const response = await optionsAPI.getSteelCategoryOptions();
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const getSteelCategoryTypeOptions = createAsyncThunk('Options/getSteelCategoryTypeOptions', async (_, thunkAPI) => {
  try {
    const response = await optionsAPI.getSteelCategoryTypeOptions();
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const getCustomerOptions = createAsyncThunk('Options/getCustomerOptions', async (params: GetCustomerParams, thunkAPI) => {
  try {
    const response = await optionsAPI.getCustomerOptions(params);
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const getCustomer1Options = createAsyncThunk('Options/getCustomer1Options', async (_, thunkAPI) => {
  try {
    const response = await optionsAPI.getCustomer1Options();
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const getCustomer2Options = createAsyncThunk('Options/getCustomer2Options', async (_, thunkAPI) => {
  try {
    const response = await optionsAPI.getCustomer2Options();
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const getCustomer3Options = createAsyncThunk('Options/getCustomer3Options', async (_, thunkAPI) => {
  try {
    const response = await optionsAPI.getCustomer3Options();
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const getLanguageOptions = createAsyncThunk('Options/getLanguageOptions', async (_, thunkAPI) => {
  try {
    const response = await optionsAPI.getLanguageOptions();
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const getTradingCompanyOptions = createAsyncThunk('Options/getTradingCompanyOptions', async (_, thunkAPI) => {
  try {
    const response = await optionsAPI.getTradingCompanyOptions();
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export default slice;
